<template>
  <b-form class="px-2 mb-3">
    <DineInBasicDetailsForm
      ref="basicDetails"
      @setUntouchedForm="setUntouchedForm"
    />
    <DineInQRCodeForm ref="qrCode" @setUntouchedForm="setUntouchedForm" />
  </b-form>
</template>

<script>
import {
  defineComponent,
  onMounted,
  onBeforeUnmount,
  nextTick,
  computed,
  ref,
} from "@vue/composition-api";
import DineInBasicDetailsForm from "./DineInBasicDetailsForm.vue";
import DineInQRCodeForm from "./DineInQRCodeForm.vue";
import _ from "lodash";

export default defineComponent({
  components: {
    DineInBasicDetailsForm,
    DineInQRCodeForm,
  },
  setup(props, { root, refs }) {
    const apiErrors = computed(
      () => root.$store.state.storeDineIn.errors || {}
    );

    const routeName = computed(() => root.$route.name || null);
    const isRouteNameCreateGroupTable = computed(
      () =>
        (routeName.value && routeName.value === "DineInCreateTableGroup") ||
        false
    );
    const isRouteNameUpdateGroupTable = computed(
      () =>
        (routeName.value && routeName.value === "DineInUpdateTableGroup") ||
        false
    );

    const title = computed(() => {
      if (isRouteNameCreateGroupTable.value) return "Create Table Group?";
      else if (isRouteNameUpdateGroupTable.value) return "Update Table Group?";
    });

    const message = computed(() => {
      if (isRouteNameCreateGroupTable.value)
        return "Are you sure you want to create a table group?";
      else if (isRouteNameUpdateGroupTable.value)
        return "Are you sure you want to save changes to this table group?";
    });

    const yesLabel = computed(() => {
      if (isRouteNameCreateGroupTable.value) return "Create";
      else if (isRouteNameUpdateGroupTable.value) return "Update";
    });

    const validateTableCreation = () => {
      root.$on("validateTableCreation", () => {
        nextTick(() => {
          if (!refs.basicDetails.v$.$invalid && !refs.qrCode.v$.$invalid) {
            root.$emit("bv::show::modal", "dine-in-prompts", {
              title: title.value,
              message: message.value,
              buttons: [
                {
                  label: "Cancel",
                  variant: "ghost btn-cancel",
                  action: "closeModal",
                },
                {
                  label: yesLabel.value,
                  variant: "primary",
                  action: "handleSaveTableGroup",
                },
              ],
            });
          }
        });
      });
    };

    const redirectDineInList = () => {
      if (_.isEmpty(apiErrors.value)) {
        setTimeout(() => {
          root.$router.push({ name: "DineInList" });
        }, 1000);
      }
    };

    const createGroupTable = async (payload) => {
      await root.$store.dispatch("storeDineIn/createGroupTable", payload);

      root.$emit("bv::hide::modal", "dine-in-prompts");

      redirectDineInList();
    };

    const updateGroupTable = async (payload) => {
      await root.$store.dispatch("storeDineIn/updateGroupTable", payload);

      root.$emit("bv::hide::modal", "dine-in-prompts");

      redirectDineInList();
    };

    const submitTableGroup = () => {
      root.$on("submitTableGroup", () => {
        const { branch_uuid, no_of_tables } = refs.basicDetails.form;
        const uuid = root.$route.params.uuid || null;
        let payload = {
          ...refs.basicDetails.form,
          branch_uuid: branch_uuid.branch_uuid,
          no_of_tables: Number(no_of_tables),
          is_qr_code_design_enable: refs.qrCode.form.is_qr_code_design_enable,
        };

        if (refs.qrCode.form.is_qr_code_design_enable) {
          payload = {
            ...payload,
            ...refs.qrCode.form,
          };
        }

        if (uuid)
          payload = {
            ...payload,
            branch_table_uuid: uuid,
          };

        if (routeName.value && routeName.value === "DineInCreateTableGroup")
          createGroupTable(payload);
        else if (
          routeName.value &&
          routeName.value === "DineInUpdateTableGroup"
        )
          updateGroupTable(payload);
      });
    };

    const untouchedForm = ref({
      basicDetails: {},
      qrCode: {},
    });

    const setUntouchedForm = ({ basicDetails = null, qrCode = null }) => {
      root.$store.commit("storeDineIn/loading", true);
      if (basicDetails) untouchedForm.value.basicDetails = basicDetails;
      else if (qrCode) untouchedForm.value.qrCode = qrCode;
      root.$store.commit("storeDineIn/loading", false);
    };

    const isUntouchedForm = () => {
      root.$on("isUntouchedForm", () => {
        const newForm = {
          basicDetails: JSON.stringify(refs.basicDetails.form),
          qrCode: JSON.stringify(refs.qrCode.form),
        };
        if (JSON.stringify(untouchedForm.value) === JSON.stringify(newForm))
          root.$emit("untouchedForm");
        else root.$emit("touchedForm");
      });
    };

    onMounted(() => {
      const formFooter = document.getElementById("dine-in-form-footer");

      if (formFooter)
        document.documentElement.style.setProperty(
          "--form-footer-height",
          `${formFooter.clientHeight}px`
        );

      validateTableCreation();

      submitTableGroup();
      isUntouchedForm();
    });

    onBeforeUnmount(() => {
      root.$off("validateTableCreation");
      root.$off("submitTableGroup");
      root.$off("isUntouchedForm");
    });

    return {
      setUntouchedForm,
    };
  },
});
</script>

<style lang="scss" scoped>
form {
  margin-bottom: var(--form-footer-height) !important;
}
</style>

<style lang="scss">
.dine-in-table-creation-section-labels,
.dine-in-table-creation-labels {
  font-family: MainFontBold, sans-serif;
  &:not(.dine-in-table-creation-section-labels) {
    font-size: 0.85em;
  }
}
.form-control {
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-background-clip: text;
    transition: background-color 5000s ease-in-out 0s;
  }
}
</style>
